import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AppShellService } from '@core/services/app-shell.service';
import { BannerService } from '@core/services/banner.service';
import { CardsService } from '@core/services/cards.service';
import { CardPageTypes } from '@core/typings/permission.typing';
import { FooterService } from '@core/services/footer.service';
import { LinkService } from '@core/services/link.service';
import { SessionKeepAliveService } from '@core/services/session-keep-alive.service';
import { SpinnerService } from '@core/services/spinner.service';
import { TokenTimeoutService } from '@core/services/token/token-timeout.service';
import { TokenService } from '@core/services/token/token.service';
import { APIAdminClient } from '@core/typings/api/admin-client.typing';
import { ClientBrandingForUi } from '@core/typings/branding.typing';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { DashboardsService } from '@features/dashboards/dashboards.service';
import { ImpersonationService } from '@features/impersonation/impersonation.service';
import { PageHelpModalService } from '@features/page-help-modals/page-help-modals.service';
import { HelpContentService } from '@features/platform-admin/help-content/help-content.service';
import { InAppAlertService } from '@features/platform-admin/in-app-alerts/services/in-app-alerts.service';
import { ReleaseNoteService } from '@features/platform-admin/release-notes/services/release-notes.service';
import { UserService } from '@features/users/user.service';
import { FooterStates } from '@yourcause/common';
import { Subscription, tap } from 'rxjs';
import { PolicyService } from '../../services/policy.service';
import { User, UserAccessTypes } from '../../typings/client-user.typing';

@Component({
  selector: 'gc-management-wrapper',
  templateUrl: './management-wrapper.component.html',
  styleUrls: ['./management-wrapper.component.scss']
})
export class ManagementWrapperComponent implements OnInit, OnDestroy {
  sidebarLinks$ = this.appShellService.changesTo$('sidebarLinks');
  sidebarClosed$ = this.appShellService.changesTo$('sidebarClosed');
  footerState$ = this.footerService.changesTo$('footerState');
  footerActionLabel$ = this.footerService.changesTo$('footerActionLabel');
  footerCancelLabel$ = this.footerService.changesTo$('footerCancelLabel');
  footerSecondaryLabel$ = this.footerService.changesTo$('footerSecondaryLabel');
  footerSecondaryDisabled$ = this.footerService.changesTo$('footerSecondaryDisabled');
  footerTertiaryLabel$ = this.footerService.changesTo$('footerTertiaryLabel');
  footerTertiaryDisabled$ = this.footerService.changesTo$('footerTertiaryDisabled');
  footerQuaternaryLabel$ = this.footerService.changesTo$('footerQuaternaryLabel');
  footerQuaternaryDisabled$ = this.footerService.changesTo$('footerQuaternaryDisabled');
  footerActionDisabled$ = this.footerService.changesTo$('footerActionDisabled');
  footerAction$ = this.footerService.changesTo$('footerAction');
  footerCancelAction$ = this.footerService.changesTo$('footerCancelAction');
  footerSecondaryAction$ = this.footerService.changesTo$('footerSecondaryAction');
  footerTertiaryAction$ = this.footerService.changesTo$('footerTertiaryAction');
  footerQuaternaryAction$ = this.footerService.changesTo$('footerQuaternaryAction');
  footerInfoText$ = this.footerService.changesTo$('footerInfoText');
  footerInfoIcon$ = this.footerService.changesTo$('footerInfoIcon');
  footerInfoTooltip$ = this.footerService.changesTo$('footerInfoTooltip');
  banners$ = this.bannerService.changesTo$('banners');
  supportLinks$ = this.appShellService.changesTo$('supportLinks');
  FOOTER_STATES = FooterStates;
  copyrightText = this.footerService.getCopyrightText();
  showHeaderButtons$ = this.appShellService.changesTo$('showHeaderButtons');

  navToggled: boolean;
  loading = false;
  timeRemaining$ = this.timeoutService.timeRemaining$.pipe(
    tap(time => {
      if (time <= 0) {
        this.onSignOutClick();
      }
    }));
  showTimeout$ = this.timeoutService.showTimeout$;
  hasNominations = false;
  user: User;
  branding: ClientBrandingForUi;
  logoLocation: string;
  initials: string;
  isBBGM = this.clientSettingsService.isBBGM;
  privacyPolicyLink = this.linkService.getPrivacyPolicyLink();
  termsAndConditionsLink = this.linkService.getTermsOfServiceLink();
  sub = new Subscription();
  manageFiltersLink: string;

  constructor (
    private sessionKeepAliveService: SessionKeepAliveService,
    private tokenService: TokenService,
    private spinnerService: SpinnerService,
    private appShellService: AppShellService,
    private router: Router,
    private policyService: PolicyService,
    private clientSettingsService: ClientSettingsService,
    private userService: UserService,
    private footerService: FooterService,
    private timeoutService: TokenTimeoutService,
    private dashboardService: DashboardsService,
    private impersonationService: ImpersonationService,
    private bannerService: BannerService,
    private cardsService: CardsService,
    private releaseNoteService: ReleaseNoteService,
    private pageHelpModalService: PageHelpModalService,
    private linkService: LinkService,
    private helpContentService: HelpContentService,
    private inAppAlertService: InAppAlertService
  ) {
    this.sub.add(this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.pageHelpModalService.checkIfWeShouldOpenPageHelpModal();
      }
    }));
  }

  get accountName () {
    return this.userService.get('userName');
  }

  get accountRole () {
    return this.userService.get('userJobTitle');
  }

  async ngOnInit () {
    this.sub.add(
      this.clientSettingsService.changesTo$('clientBranding').subscribe(() => {
        this.branding = this.clientSettingsService.get('clientBranding');
        this.logoLocation = this.branding ? this.branding.logoUrl : '';
      })
    );
    this.spinnerService.startSpinner();

    this.sub.add(
      this.userService.changesTo$('user').subscribe(async (user: User) => {
        const loggedIn = user && this.tokenService.getIsLoggedIn();
        if (loggedIn) {
          this.initials = user.firstName[0] + user.lastName[0];
          if (location.pathname.includes('auth')) {
            this.router.navigateByUrl(this.dashboardService.homeRoute);
          }
          this.manageFiltersLink = user.accessType === UserAccessTypes.Default ?
            '/management/manage-filters' :
            '';
          this.hasNominations = this.clientSettingsService.doesClientHaveClientFeature(APIAdminClient.ClientFeatureTypes.HasNominations);
          this.user = user;

          this.timeoutService.start();
          this.sessionKeepAliveService.startWatching();
          if (this.tokenService.hasImpersonationToken()) {
            this.impersonationService.startPollingEndedImpersonationSession();
          }
        } else {
          this.initials = '';
          this.timeoutService.stop();
          this.sessionKeepAliveService.stopWatching();
          setTimeout(() => {
            this.impersonationService.stopPollingEndedImpersonationSession();
          }, 700);
        }
        this.setBannerInfo();
        this.setSidebarLinks();
        this.spinnerService.stopSpinner();
        if (loggedIn) {
          if (!this.isBBGM) {
            this.handleInAppAlerts();
            this.handleReleaseNotes();
          }
          this.setHelpContent();
        }
      })
    );
    this.sub.add(this.dashboardService.changesTo$('homeRoute').subscribe(() => {
      if (this.appShellService.sidebarLinks) {
        this.setSidebarLinks();
      }
    }));
  }

  async setHelpContent () {
    await this.helpContentService.getContents();
    this.appShellService.setSupportLinks();
    this.appShellService.registerSupportLinkListener();
  }

  async handleReleaseNotes () {
    await this.releaseNoteService.setReleaseNoteForManager();
    await this.releaseNoteService.handleOpenReleaseNoteModal();
  }

  async handleInAppAlerts () {
    await this.inAppAlertService.setInAppAlert();
    await this.inAppAlertService.handleOpenInAppAlertModal();
  }

  setSidebarLinks () {
    const showInsights = this.cardsService.canSeePage(CardPageTypes.INSIGHTS);
    const showApplicationManager =  this.policyService.grantApplication.canAccessApplicationManager();
    const canManageProcessing = this.policyService.processing.canManageProcessing();
    const canViewDisbursementReport = this.policyService.processing.canViewDisbursementReport();
    const canSeeProgramSetup = this.cardsService.canSeePage(CardPageTypes.PROGRAM);
    const canSeeSettings = this.cardsService.canSeePage(CardPageTypes.SETTINGS);
    const canSeeReporting = this.policyService.dataExport.canPullApplicationDetails() ||
      this.policyService.dataExport.canPullAwardExport() ||
      this.policyService.dataExport.canPullBudgetExport() ||
      this.policyService.dataExport.canPullFormExport() ||
      this.policyService.dataExport.canPullPaymentExport() ||
      this.policyService.dataExport.canPullProgramSummaryExport() ||
      this.policyService.dataExport.canManageAdHoc() ||
      this.userService.user?.hasReports;
    this.appShellService.setSidebarLinks([{
      label: 'Home',
      labelClass: '',
      i18nKey: 'NAV:Home',
      href: this.dashboardService.homeRoute,
      icon: 'home',
      sequence: '1.0',
      group: 1
    }, showApplicationManager ? {
      label: 'Applications',
      i18nKey: 'common:hdrApplications',
      href: '/management/manage-applications/applications',
      icon: 'clipboard-list',
      sequence: '1.2',
      group: 2
    } : null, showApplicationManager &&
      this.clientSettingsService.doesClientHaveClientFeature(APIAdminClient.ClientFeatureTypes.HasNominations) &&
      (this.userService.get('user') as User).isInNominationWorkFlow ? {
      label: 'Nominations',
      i18nKey: 'common:hdrNominations',
      href: '/management/manage-nominations/nominations',
      icon: 'medal',
      sequence: '',
      group: 2
    } : null, showInsights ? {
      label: 'Insights',
      i18nKey: 'GLOBAL:textInsights',
      href: '/management/insights',
      icon: 'chart-line',
      sequence: '1.3',
      group: 2
    } : null, canSeeReporting ? {
      label: 'Reporting',
      i18nKey: 'common:hdrReporting',
      href: '/management/reporting',
      icon: 'file-chart-line',
      sequence: '1.5',
      group: 3
    } : null, canManageProcessing || canViewDisbursementReport ? {
      label: 'Payment Processing',
      i18nKey: 'common:hdrPaymentProcessing',
      href: canManageProcessing ?
        '/management/payment-processing/available' :
        '/management/payment-processing/disbursement-report',
      icon: 'money-bill',
      sequence: '',
      group: 3
    } : null, canSeeProgramSetup ? {
      label: 'Program Setup',
      i18nKey: 'GLOBAL:textProgramSetup',
      href: '/management/program-setup',
      icon: 'toolbox',
      sequence: '',
      group: 4
    } : null, canSeeSettings ? {
      label: 'Settings',
      i18nKey: 'GLOBAL:textSettings',
      href: '/management/configure',
      icon: 'cog',
      sequence: '1.4',
      group: 4
    } : null].filter(link => !!link));
  }

  setBannerInfo () {
    this.bannerService.setManagerPortalBanners();
  }

  onSignOutClick () {
    this.tokenService.logout();
  }

  keepMeSignedIn () {
    this.tokenService.getLatestToken();
  }

  handleSidebarToggle () {
    this.appShellService.toggleSidebar();
  }

  ngOnDestroy () {
    this.sub.unsubscribe();
  }
}
